import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Button, Tag, Descriptions, Spin, message, Card,
  Form, Select, DatePicker, Input, Space, Divider, Alert,
} from 'antd';
import TimeSelect from '../components/TimeSelect';
import { SaveOutlined, CloseCircleOutlined, CheckOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import LinkifiedText from '../components/LinkifiedText';
import { useBackFallback, usePageLabel } from '../components/BackChip';

const REASON_OPTIONS = [
  'Sick/Health Related', 'Jury Duty', 'Bereavement', 'Outage', 'Other',
].map((v) => ({ label: v, value: v }));

const PERIOD_OPTIONS = [
  'Entire Day', 'Absent Part of Day',
].map((v) => ({ label: v, value: v }));

const STATUS_COLOR = { Submitted: 'blue', Acknowledged: 'green', Canceled: 'default' };

function parseTime(str) {
  if (!str) return null;
  const parsed = dayjs(str, 'h:mm A');
  return parsed.isValid() ? parsed : dayjs(str, 'H:mm');
}

export default function AbsenceNotificationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  // The Back chip (Layout): back where you came from, else this list.
  const staffAbs = currentUser?.Role !== 'agent' && (currentUser?.CanEditAbsences ?? 0) >= 1;
  useBackFallback(staffAbs ? '/absences' : '/my-absences', staffAbs ? 'Absence Notifications' : 'My Absence Notifications');
  usePageLabel(`Absence Notification #${id}`);
  const isAgent = currentUser?.Role === 'agent';
  const isAdmin = ['admin', 'qa_admin'].includes(currentUser?.Role);

  const [absence, setAbsence] = useState(null);
  const [comments, setComments] = useState([]);
  const [canEditFields, setCanEditFields] = useState(false);
  const [canCancel, setCanCancel] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editForm] = Form.useForm();
  const [editPeriod, setEditPeriod] = useState('');
  const [allTeams, setAllTeams] = useState([]);
  const [shiftSuggestion, setShiftSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/absences/${id}`);
      const { absence: a, comments: c, can_edit_fields, can_cancel } = res.data;
      setAbsence(a);
      setComments(c);
      setCanEditFields(can_edit_fields);
      setCanCancel(can_cancel);
      setEditPeriod(a.AbsencePeriod);
      if (!allTeams.length) {
        await api.get('/api/teams').then((r) => setAllTeams(r.data || [])).catch(() => {});
      }
      const resolvedTeamId = a.TeamID || a.UserTeamID || null;
      editForm.setFieldsValue({
        reason:         a.Reason,
        absence_period: a.AbsencePeriod,
        date_start:     a.DateStart ? dayjs(a.DateStart) : null,
        date_end:       a.DateEnd   ? dayjs(a.DateEnd)   : null,
        start_time:     parseTime(a.StartTime),
        end_time:       parseTime(a.EndTime),
        team_id:        resolvedTeamId || undefined,
      });
      // Fetch scheduled shift for that date so we can offer a prefill
      if (a.DateStart && a.UserID && resolvedTeamId) {
        api.get('/api/schedule/shifts', {
          params: { team_ids: resolvedTeamId, date: a.DateStart },
        }).then((r) => {
          const shift = (r.data || []).find((s) => s.UserID === a.UserID);
          if (shift?.ShiftStart && shift?.ShiftEnd) {
            const fmt = (hhmm) => {
              const [h, m] = hhmm.split(':').map(Number);
              return dayjs().startOf('day').hour(h).minute(m);
            };
            setShiftSuggestion({
              start: fmt(shift.ShiftStart),
              end:   fmt(shift.ShiftEnd),
              label: `${fmt(shift.ShiftStart).format('h:mm A')} – ${fmt(shift.ShiftEnd).format('h:mm A')}`,
            });
          }
        }).catch(() => {});
      }
    } catch (err) {
      if (err.response?.status === 403) {
        message.error('You do not have access to this absence notification.');
        navigate(-1);
      } else {
        message.error('Failed to load absence notification.');
      }
    } finally {
      setLoading(false);
    }
  }, [id, editForm, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSaveFields = async () => {
    let values;
    try { values = await editForm.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        reason:         values.reason,
        absence_period: values.absence_period,
        start_time:     values.start_time?.format('h:mm A') || null,
        end_time:       values.end_time?.format('h:mm A') || null,
        team_id:        values.team_id || null,
        ...(isAdmin && values.date_start ? { date_start: values.date_start.format('YYYY-MM-DD') } : {}),
        ...(isAdmin && values.date_end   ? { date_end:   values.date_end.format('YYYY-MM-DD')   } : {}),
      };
      await api.put(`/api/absences/${id}`, body);
      message.success('Changes saved.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.put(`/api/absences/${id}`, { status: 'Canceled' });
      message.success('Absence cancelled.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to cancel absence.');
    } finally {
      setCancelling(false);
    }
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await api.put(`/api/absences/${id}`, { status: 'Acknowledged' });
      message.success('Absence acknowledged.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to acknowledge absence.');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleAddComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setAddingComment(true);
    try {
      await api.post(`/api/absences/${id}/comments`, { comment: text });
      setCommentText('');
      const res = await api.get(`/api/absences/${id}`);
      setComments(res.data.comments);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!absence) return null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Absence Notification #{absence.ID}
        </Typography.Title>
        <Tag color={STATUS_COLOR[absence.Status] ?? 'default'} style={{ fontSize: 13 }}>
          {absence.Status}
        </Tag>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {(currentUser?.CanEditAbsences ?? 0) >= 2 && currentUser?.ID !== absence.UserID && absence.Status === 'Submitted' && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleAcknowledge}
              loading={acknowledging}
            >
              Acknowledge
            </Button>
          )}
          {canCancel && (
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={handleCancel}
              loading={cancelling}
            >
              Cancel Absence
            </Button>
          )}
        </div>
      </div>

      <Card style={{ marginBottom: 16 }}>
        {canEditFields ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Typography.Text><Typography.Text strong>Employee:</Typography.Text> {absence.UserName}</Typography.Text>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveFields}
                loading={saving}
              >
                Save Changes
              </Button>
            </div>
            <Form
              form={editForm}
              layout="vertical"
              onValuesChange={(changed) => {
                if (changed.start_time) {
                  const start = changed.start_time;
                  const end = editForm.getFieldValue('end_time');
                  if (end && dayjs.isDayjs(end) && !end.isAfter(start)) {
                    editForm.setFieldValue('end_time', start.add(30, 'minute'));
                  }
                }
              }}
            >
              <Form.Item label="Team" name="team_id" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch placeholder="Select team…"
                  options={allTeams.map((t) => ({ label: t.Name, value: t.ID }))}
                  filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                  disabled={isAgent || !!absence.TeamID}
                />
              </Form.Item>
              <Form.Item label="Reason" name="reason" rules={[{ required: true, message: 'Required' }]}>
                <Select options={REASON_OPTIONS} />
              </Form.Item>
              <Form.Item label="Absence Period" name="absence_period" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  options={PERIOD_OPTIONS}
                  onChange={(v) => setEditPeriod(v)}
                />
              </Form.Item>
              <Form.Item label="Date Start" name="date_start" rules={isAdmin ? [{ required: true, message: 'Required' }] : []}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled={!isAdmin} />
              </Form.Item>

              {shiftSuggestion && (
                <Form.Item style={{ marginTop: -8 }}>
                  <Alert
                    type="info"
                    style={{ padding: '6px 12px' }}
                    message={
                      <span style={{ fontSize: 13 }}>
                        Scheduled shift: <strong>{shiftSuggestion.label}</strong>
                        <Button
                          size="small" type="link" style={{ padding: '0 6px' }}
                          onClick={() => editForm.setFieldsValue({ start_time: shiftSuggestion.start, end_time: shiftSuggestion.end })}
                        >
                          Use these times
                        </Button>
                      </span>
                    }
                  />
                </Form.Item>
              )}

              {editPeriod === 'Entire Day' && (
                <>
                  <Form.Item label="Shift Start Time (EST)" name="start_time" rules={[{ required: true, message: 'Required' }]}>
                    <TimeSelect />
                  </Form.Item>
                  <Form.Item label="Shift End Time (EST)" name="end_time" rules={[{ required: true, message: 'Required' }]}>
                    <TimeSelect />
                  </Form.Item>
                </>
              )}
              {editPeriod === 'Absent Part of Day' && (
                <>
                  <Form.Item label="Start Time of Absence (EST)" name="start_time" rules={[{ required: true, message: 'Required' }]}>
                    <TimeSelect />
                  </Form.Item>
                  <Form.Item label="End Time of Absence (EST)" name="end_time" rules={[{ required: true, message: 'Required' }]}>
                    <TimeSelect />
                  </Form.Item>
                </>
              )}
            </Form>
          </>
        ) : (
          <>
            <Typography.Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
              Details
            </Typography.Text>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Employee">{absence.UserName}</Descriptions.Item>
              <Descriptions.Item label="Team">{absence.TeamName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Reason">{absence.Reason}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[absence.Status] ?? 'default'}>{absence.Status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Absence Period">{absence.AbsencePeriod}</Descriptions.Item>
              <Descriptions.Item label="Date Start">{absence.DateStart || '—'}</Descriptions.Item>
              <Descriptions.Item label="Date End">
                {absence.AbsencePeriod === 'Multiple Consecutive Days' ? (absence.DateEnd || '—') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={absence.AbsencePeriod === 'Absent Part of Day' ? 'Absence Start Time' : 'Shift Start Time'}>
                {absence.StartTime || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={absence.AbsencePeriod === 'Absent Part of Day' ? 'Absence End Time' : 'Shift End Time'}>
                {absence.EndTime || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted">{formatTS(absence.CreatedAt)}</Descriptions.Item>
              <Descriptions.Item label="Last Updated">{formatTS(absence.UpdatedAt)}</Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Card>

      <Card>
        <Typography.Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
          Comments
        </Typography.Text>

        {comments.length === 0 && (
          <Typography.Text type="secondary">No comments yet.</Typography.Text>
        )}

        {comments.map((c) => (
          <div key={c.ID} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <Space>
              <Typography.Text strong>{c.UserName}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
            </Space>
            <div style={{ marginTop: 4 }}><LinkifiedText text={c.Comment} /></div>
          </div>
        ))}

        <Divider style={{ margin: '12px 0' }} />

        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Add a comment…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onPressEnter={handleAddComment}
          />
          <Button
            type="primary"
            onClick={handleAddComment}
            loading={addingComment}
            disabled={!commentText.trim()}
          >
            Add
          </Button>
        </Space.Compact>
      </Card>
    </>
  );
}
